import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react'
import { keepPreviousData, useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { CircleFlag } from 'react-circle-flags'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { fetchTeamList } from '../../api/workforce/workforce'
import { fetchAllEmployeeEvaluations } from '../../api/evaluations/evaluations'
import { ChevronDownIcon } from '../../assets/icons'
import { getCountryNameByCode, phoneNumbers } from '../countries/PhoneDial'
import AvatarCell from './base/AvatarCell'
import FilterButton from './base/FilterButton'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import formatDate from '../../helpers/formatDate'

// prop for passing page where user was before navigating away
interface EvaluationsTableProps {
  initialPage?: number
}

const EvaluationsTable: React.FC<EvaluationsTableProps> = ({ initialPage = 1 }) => {
  const { t } = useTranslation()
  const [selectedRowsActive, setSelectedRowsActive] = useState<number[]>([])
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({ active: ['true'] })
  const isExporting = false
  const navigate = useNavigate()
  const [sortBy, setSortBy] = useState<'recent' | 'alpha_asc' | 'alpha_desc'>('recent')
  const [evaluationStatus, setEvaluationStatus] = useState<'published' | 'draft' | null>(null)
  const [page, setPage] = useState(initialPage) // either 1 or the page user was on before navigating away
  const [searchTerm, setSearchTerm] = useState('')
  const itemsPerPage = 15
  const queryClient = useQueryClient()

  const derivedFilters = useMemo(() => {
    const next = { ...filters }

    if (evaluationStatus) {
      next.status = [evaluationStatus]
    } else {
      delete next.status
    }

    return next
  }, [filters, evaluationStatus])
  const derivedFiltersKey = useMemo(() => JSON.stringify(derivedFilters), [derivedFilters])

  // --- Sort options ---
  const SORT_OPTIONS = useMemo(
    () =>
      [
        { id: 'recent', label: t('evaluations.sort.recentlyEvaluated') },
        { id: 'alpha_asc', label: t('evaluations.sort.alphaAsc') },
        { id: 'alpha_desc', label: t('evaluations.sort.alphaDesc') },
      ] as const,
    [t],
  )

  const isSortModified = sortBy !== 'recent'
  const isStatusModified = !!evaluationStatus

  // --- Filter state ---
  const updateFilter = (key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  // --- Infinite scroll filter data: Teams ---
  const [teamsFilterSearch, setTeamsFilterSearch] = useState('')
  const teamsFilterQuery = useInfiniteQuery({
    queryKey: ['filter-teams', 10, teamsFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchTeamList({
        search: teamsFilterSearch,
        page: pageParam,
        pageSize: 10,
        filters: {},
        fields: 'id,name',
        expand: '',
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
  })
  const teamsFilterList = useMemo(
    () =>
      (teamsFilterQuery.data?.pages ?? []).flatMap((p) =>
        Array.isArray(p?.data) ? p.data.map((t: { id: number; name: string }) => ({ id: t.id, name: t.name })) : [],
      ),
    [teamsFilterQuery.data?.pages],
  )

  // --- Static filter data: Country ---
  const countryFilterList = useMemo(() => phoneNumbers.map((c) => ({ id: c.code, name: c.name })), [])

  const ordering =
    sortBy === 'recent'
      ? '-created_at'
      : sortBy === 'alpha_asc'
        ? ['employee__first_name', 'employee__last_name']
        : ['-employee__first_name', '-employee__last_name']

  const people = useQuery({
    queryKey: ['evaluation-summary', page, searchTerm, derivedFiltersKey, sortBy],
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 60 * 1000, // 1 minute - keep recently prefetched pages fresh
    placeholderData: keepPreviousData,
    queryFn: () =>
      fetchAllEmployeeEvaluations({
        page,
        page_size: itemsPerPage,
        search: searchTerm,
        filters: derivedFilters,
        ordering,
      }),
  })

  // Prefetch next page when current page looks full (likely more data)
  React.useEffect(() => {
    const currentCount = people.data?.data?.length ?? 0
    if (currentCount < itemsPerPage) return

    const nextPage = page + 1

    queryClient.prefetchQuery({
      queryKey: ['evaluation-summary', nextPage, searchTerm, derivedFiltersKey, sortBy],
      staleTime: 60 * 1000, // keep prefetched data fresh for the same duration
      queryFn: () =>
        fetchAllEmployeeEvaluations({
          page: nextPage,
          page_size: itemsPerPage,
          search: searchTerm,
          filters: derivedFilters,
          ordering,
        }),
    })
  }, [people.data, page, searchTerm, derivedFilters, derivedFiltersKey, sortBy, queryClient, itemsPerPage, ordering])

  const tableData = people?.data?.data || []

  const columns: Column[] = [
    {
      label: t('common.fields.name'),
      accessor: 'name',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.employee?.profile_picture} name={`${row.employee?.first_name || ''} ${row.employee?.last_name || ''}`.trim()} />
      ),
    },
    {
      label: t('evaluations.table.columns.teams'),
      accessor: 'teams',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => {
        const membershipTeamNames = row.employee?.teams?.map((team: { name: string }) => team.name) || []

        const managedTeamNames = row.employee?.managed_teams?.map((team: { name: string }) => team.name) || []

        const teamNames = Array.from(new Set([...membershipTeamNames, ...managedTeamNames]))

        return (
          <Text textStyle="l-xs" isTruncated>
            {teamNames.length > 1 ? (
              <>
                {teamNames.slice(0, 1).join(', ')}
                <Text as="span" color="solid.400" textStyle="l-xs" ml="8px">
                  {t('common.labels.moreCount', { count: teamNames.length - 1 })}
                </Text>
              </>
            ) : (
              teamNames.length !== 0 ? teamNames.join(', ') : t('common.labels.na')
            )}
          </Text>
        )
      },
    },
    {
      label: t('evaluations.table.columns.countryOfWork'),
      accessor: 'country_of_work',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <Flex alignItems="center">
          {row.employee.country_of_work && (
            <CircleFlag
              countryCode={row.employee.country_of_work?.toLowerCase()}
              style={{ width: '24px', height: '24px', marginRight: '8px' }}
            />
          )}
          <Text isTruncated textStyle="l-xs">
            {row?.employee?.country_of_work ? getCountryNameByCode(row?.employee?.country_of_work) : t('common.labels.na')}
          </Text>
        </Flex>
      ),
    },
    {
      label: t('evaluations.table.columns.lastEvaluationDate'),
      accessor: 'date',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs">
          {row?.status === 'published' && row?.created_at ? formatDate(row.created_at) : t('common.labels.na')}
        </Text>
      ),
    },
    {
      label: t('evaluations.table.columns.evaluator1'),
      accessor: 'primary_evaluator',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.employee?.primary_evaluator?.profile_picture} name={`${row.employee?.primary_evaluator?.first_name || ''} ${row.employee?.primary_evaluator?.last_name || ''}`.trim() || t('common.labels.na')} />
      ),
    },
    {
      label: t('evaluations.table.columns.evaluator2'),
      accessor: 'secondary_evaluator',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.employee?.secondary_evaluator?.profile_picture} name={`${row.employee?.secondary_evaluator?.first_name || ''} ${row.employee?.secondary_evaluator?.last_name || ''}`.trim() || t('common.labels.na')} />
      ),
    },
    {
      label: t('evaluations.table.columns.lastGeneralScore'),
      accessor: 'general_score',
      type: 'custom',
      width: '15%',
      Cell: ({ row }: { row: RowData }) => {
        const score = typeof row.general_score === 'number' ? row.general_score : 0
        const filledCount = Math.floor(score / 20)
        const green = 'success.100'
        const gray = 'solid.100'
        return (
          <Flex direction="column" align="center" minH="38px" justify="center">
            <Flex align="center" gap="8px">
              {Array.from({ length: 5 }).map((_, idx) => (
                <Box
                  key={idx}
                  minWidth="20px"
                  maxWidth="20px"
                  height="20px"
                  width="20px"
                  borderRadius="full"
                  borderWidth="1px"
                  borderColor={idx < filledCount ? green : gray}
                  bg={idx < filledCount ? green : 'white'}
                  sx={{
                    opacity: 1,
                    color: 'inherit',
                  }}
                />
              ))}
            </Flex>
          </Flex>
        )
      },
    },
    { label: '', accessor: 'actions', type: 'actions' },
  ]

  const handleSearch = (searchTerm: string) => {
    setPage(1)
    setSearchTerm(searchTerm)
  }

  const handleOpenNewTab = (row: RowData) => {
    const path = `/user/workforce/people/${row.employee.id}?tab=evaluation`
    window.open(path, '_blank', 'noopener,noreferrer')
  }

  const handleRowClick = (row: RowData) => {
    navigate(`/user/workforce/people/${row.employee.id}`, { state: { tab: 'evaluation' } })
  }

  const handleSelectRow = (id: string | number, isChecked: boolean) => {
    const numId = typeof id === 'string' ? parseInt(id, 10) : id
    if (isChecked) {
      setSelectedRowsActive((prev) => [...prev, numId])
    } else {
      setSelectedRowsActive((prev) => prev.filter((rowId) => rowId !== numId))
    }
  }

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedRowsActive(tableData.map((row: RowData) => row.id))
    } else {
      setSelectedRowsActive([])
    }
  }

  return (
    <Card width="100%" p="0">
      <Grid>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          height="64px"
          flexWrap="wrap"
          gap={4}
          minW={0}
          px="20px"
        >
          <SearchInput onSearch={handleSearch} />

          <Flex gap="8px" flexShrink={0}>
            <FilterButton
              label={t('common.fields.team')}
              options={teamsFilterList}
              onApply={(selected) => updateFilter('teams', selected)}
              search={teamsFilterSearch}
              setSearch={setTeamsFilterSearch}
              onLoadMore={() => teamsFilterQuery.fetchNextPage()}
              hasMore={!!teamsFilterQuery.hasNextPage}
              isLoadingMore={teamsFilterQuery.isFetchingNextPage}
              isLoading={teamsFilterQuery.isFetching && !teamsFilterQuery.isFetchingNextPage}
            />
            <FilterButton
              label={t('common.fields.country')}
              options={countryFilterList}
              onApply={(selected) => updateFilter('country_of_work', selected)}
            />

            {/* Sort menu */}
            <Menu placement="bottom-end">
              <MenuButton
                as={Button}
                display="flex"
                alignItems="center"
                gap={2}
                sx={{
                  bg: isSortModified ? 'purple.400' : 'transparent',
                  color: isSortModified ? 'white' : 'black',
                  fontWeight: isSortModified ? '600' : '400',
                }}
                variant="white"
                disabled={!tableData.length || isExporting}
                size="sm"
                h="32px"
                px="14px"
                fontSize="13px"
                fontWeight="600"
                borderRadius="8px"
              >
                {SORT_OPTIONS.find((o) => o.id === sortBy)?.label ?? t('common.actions.sort')}
                <Icon width="12px" height="12px" fill="none" color="solid.500" ml={2}>
                  <ChevronDownIcon />
                </Icon>
              </MenuButton>
              <MenuList minW="220px">
                {SORT_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt.id}
                    onClick={() => {
                      setPage(1)
                      setSortBy(opt.id)
                    }}
                    fontWeight={sortBy === opt.id ? '600' : '400'}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            <Menu placement="bottom-end">
              <MenuButton
                as={Button}
                size="sm"
                h="32px"
                px="14px"
                fontSize="13px"
                borderRadius="8px"
                variant="white"
                fontWeight="400"
                display="flex"
                alignItems="center"
                gap={2}
                disabled={!tableData.length || isExporting}
                sx={{
                  bg: isStatusModified ? 'purple.400' : 'transparent',
                  color: isStatusModified ? 'white' : 'black',
                  fontWeight: isStatusModified ? '600' : '400',
                }}
              >
                {evaluationStatus ? t(`common.statuses.${evaluationStatus}`) : t('evaluations.filters.evaluationStatus')}
                <Icon width="12px" height="12px" fill="none" color="solid.500" ml={2}>
                  <ChevronDownIcon />
                </Icon>
              </MenuButton>

              <MenuList minW="220px">
                <MenuItem onClick={() => setEvaluationStatus('published')}>{t('common.statuses.published')}</MenuItem>
                <MenuItem onClick={() => setEvaluationStatus('draft')}>{t('common.statuses.draft')}</MenuItem>
                <MenuItem onClick={() => setEvaluationStatus(null)} fontWeight="500">
                  {t('evaluations.filters.allStatuses')}
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Flex>
        {people.isLoading ? (
          <LoadingTable columns={columns} />
        ) : (
          <Box minHeight="58vh" overflowX="auto" overflowY="visible">
            <MasterTable
              columns={columns}
              data={tableData}
              onRowClick={handleRowClick}
              onOpenNewTab={handleOpenNewTab}
              selectedRows={selectedRowsActive}
              onSelectAll={handleSelectAll}
              onSelectRow={handleSelectRow}
              allSelected={
                tableData.length > 0 && selectedRowsActive.length === tableData.length
              }
              isIndeterminate={
                tableData.length > 0 && selectedRowsActive.length > 0 && selectedRowsActive.length < tableData.length
              }
              tableType="people"
            />
          </Box>
        )}
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          maxPage={people?.data?.max_page || 0}
          totalItems={people?.data?.count || 0}
          handlePreviousPage={() => setPage(page - 1)}
          handleNextPage={() => setPage(page + 1)}
          handlePageClick={(page: number) => setPage(page)}
        />
      </Grid>
    </Card>
  )
}

export default EvaluationsTable
